// Meta Pixel + ad attribution capture.
//
// Split of responsibilities (see metaCapi.js for the server half):
//   - here:   PageView, ViewContent, InitiateCheckout — browser-side, so they
//             carry the visitor's real ad cookies and fire on every page.
//   - server: Purchase, CompleteRegistration — sent from the Stripe webhook and
//             the register endpoint, where they can't be blocked and can't be
//             faked by a visitor hitting the URL directly.
//
// Consent: the browser pixel follows the same gate as GA (utils/analytics.ts) —
// nothing loads until the visitor accepts cookies. The server-side Purchase
// event is unaffected by that banner, which is a large part of why the
// money-event lives on the server.
//
// Inert until META_PIXEL_ID is set on the backend; /api/marketing/config
// returns null and every function below becomes a no-op.

import { hasConsent } from '@/utils/analytics';

declare global {
  interface Window {
    fbq?: ((...args: any[]) => void) & { callMethod?: (...args: any[]) => void; queue?: any[]; loaded?: boolean; version?: string; push?: any };
    _fbq?: any;
  }
}

const VISITOR_KEY = 'hj_visitor_id';
const ATTRIBUTION_KEY = 'hj_attribution';
const AFFILIATE_REF_KEY = 'hj_affiliate_ref';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

let pixelLoaded = false;
let pixelIdPromise: Promise<string | null> | null = null;

// ---- Visitor identity -------------------------------------------------------

// Stable anonymous id. This is the join key that survives the gap between
// "clicked an ad" and "completed a Stripe checkout three pages later".
export function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // Private browsing with storage disabled — attribution degrades to nothing,
    // which is fine; it must never throw and break the page.
    return '';
  }
}

// Landing-page hit counter, separate from the Meta pixel on purpose.
//
// The pixel is gated on the cookie banner and blocked by content blockers, so
// on a printed QR code it counts a fraction of the scans and the fraction isn't
// knowable. This fires on every load. It reuses the same anonymous visitor id
// so a repeat scan in one day doesn't double-count, and it deliberately sends
// nothing else: no path, no referrer, no account. Never throws and never
// blocks paint — a counter is not worth a broken landing page.
export function countPageHit(page: string): void {
  try {
    const body = JSON.stringify({ page, vid: getVisitorId() });
    // keepalive so the beacon survives the tap straight through to Stripe.
    fetch('/api/track/hit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* a counter never surfaces an error */ });
  } catch { /* storage disabled, offline, whatever — not worth a word */ }
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : undefined;
}

// ---- Attribution ------------------------------------------------------------

export interface StoredAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPath?: string;
  referrer?: string;
}

function camel(param: string): keyof StoredAttribution {
  // utm_source -> utmSource
  return param.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) as keyof StoredAttribution;
}

export function getStoredAttribution(): StoredAttribution {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || '{}');
  } catch {
    return {};
  }
}

// Meta's click id cookie. The pixel normally writes _fbc itself, but it only
// does so once loaded and consented — so when we see an fbclid in the URL we
// build the value by hand (fb.1.<ms>.<fbclid> is Meta's documented format).
// This keeps click attribution working even for visitors who never accept
// cookies, since the server sends it via the Conversions API.
function resolveFbc(params: URLSearchParams): string | undefined {
  const existing = readCookie('_fbc');
  if (existing) return existing;
  const fbclid = params.get('fbclid');
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined;
}

// ---- Affiliate ref ----------------------------------------------------------

// The affiliate link a visitor arrived on (plus.heyjoe.io/charter?ref=<code>).
//
// localStorage, not sessionStorage, and captured on LANDING rather than at checkout —
// both matter. Charter checkout is gated behind login, and CharterPage sends a
// logged-out buyer to /login?returnTo=/charter, which drops the query string. By the
// time they are back, the URL no longer carries the ref; only what we stored does.
export function getStoredAffiliateRef(): string {
  try {
    return localStorage.getItem(AFFILIATE_REF_KEY) || '';
  } catch {
    return '';
  }
}

// Every landing from a campaign email, not just the first touch. The send engine
// (casting run-campaign-step.js) tags each Hey Joe link with utm_medium=email,
// utm_campaign and hj_r, a per-recipient hash. This is what answers "how many of
// the people who clicked actually reached the page": a mail filter that scans
// links never runs this script. Once per tab per campaign + page.
function recordEmailLanding(visitorId: string, params: URLSearchParams): void {
  const campaign = params.get('utm_campaign');
  if (!campaign || (params.get('utm_medium') !== 'email' && !params.get('hj_r'))) return;
  const key = `hj_email_landing:${campaign}:${window.location.pathname}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* storage disabled: send anyway */ }
  fetch('/api/marketing/email-landing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      visitorId,
      campaign,
      content: params.get('utm_content') || undefined,
      token: params.get('hj_r') || undefined,
      path: window.location.pathname,
      referrer: document.referrer || undefined,
    }),
    keepalive: true,
  }).catch(() => { /* tracking must never surface an error */ });
}

// Call once on first load. First-touch only: if we already stored a campaign we
// keep it, because the ad that originally found someone is the one that earned
// the eventual sale.
export function captureAttribution(): void {
  const visitorId = getVisitorId();
  if (!visitorId) return;

  const params = new URLSearchParams(window.location.search);
  recordEmailLanding(visitorId, params);
  const stored = getStoredAttribution();
  const isFirstTouch = !stored.utmSource && !stored.utmCampaign;

  let next = stored;
  if (isFirstTouch) {
    next = { ...stored };
    for (const p of UTM_PARAMS) {
      const v = params.get(p);
      if (v) next[camel(p)] = v as any;
    }
    // An fbclid with no utm_source is still a Meta click — label it so the
    // report doesn't file it under "direct".
    if (!next.utmSource && params.get('fbclid')) next.utmSource = 'facebook';
    if (Object.keys(next).length) {
      next.landingPath = window.location.pathname + window.location.search;
      next.referrer = document.referrer || undefined;
      try { localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next)); } catch { /* storage disabled */ }
    }
  }

  // Affiliate ref gets its own first-touch rule rather than riding `isFirstTouch`:
  // that flag is computed from the UTM fields, so a visitor who clicked an ad last
  // week would never record the affiliate link they clicked today. First ref wins,
  // and an untagged later visit never clears it.
  let affiliateRef = getStoredAffiliateRef();
  const refParam = params.get('ref');
  if (refParam && !affiliateRef) {
    affiliateRef = refParam.slice(0, 40);
    try { localStorage.setItem(AFFILIATE_REF_KEY, affiliateRef); } catch { /* storage disabled */ }
  }

  const fbc = resolveFbc(params);
  const fbp = readCookie('_fbp');

  // Only beacon when there's something worth recording — no point writing a row
  // for every organic pageview.
  if (!next.utmSource && !next.utmCampaign && !fbc && !fbp && !refParam) return;

  fetch('/api/marketing/attribution', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ visitorId, fbp, fbc, ...next, ...(affiliateRef ? { affiliateRef } : {}) }),
    keepalive: true,
  }).catch(() => { /* tracking must never surface an error */ });
}

// ---- Meta Pixel -------------------------------------------------------------

async function fetchPixelId(): Promise<string | null> {
  if (!pixelIdPromise) {
    pixelIdPromise = fetch('/api/marketing/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.metaPixelId || null)
      .catch(() => null);
  }
  return pixelIdPromise;
}

// Meta's standard snippet, transcribed rather than injected as a blob so it can
// be read and audited here.
function injectPixel(pixelId: string): void {
  if (window.fbq) return;
  const fbq: any = function (...args: any[]) {
    fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', pixelId);
}

// Loads the pixel but deliberately does NOT fire PageView — the caller owns
// that, so the first load and every subsequent SPA route change go through the
// same path and the initial view isn't counted twice.
export async function initMetaPixel(): Promise<void> {
  if (pixelLoaded || !hasConsent()) return;
  const pixelId = await fetchPixelId();
  if (!pixelId) return;
  injectPixel(pixelId);
  pixelLoaded = true;
}

export function trackPixel(event: string, params?: Record<string, any>): void {
  if (!pixelLoaded) return;
  window.fbq?.('track', event, params);
}

// ---- Named events -----------------------------------------------------------

export function trackViewContent(product: { _id: string; title?: string; price?: number }): void {
  trackPixel('ViewContent', {
    content_ids: [product._id],
    content_type: 'product',
    content_name: product.title,
    // Product prices are stored in cents; Meta expects a decimal amount.
    value: (product.price || 0) / 100,
    currency: 'USD',
  });
}

export function trackInitiateCheckout(product: { _id: string; title?: string; price?: number }): void {
  trackPixel('InitiateCheckout', {
    content_ids: [product._id],
    content_type: 'product',
    content_name: product.title,
    value: (product.price || 0) / 100,
    currency: 'USD',
  });
}

// A free seat taken on the event page, signed out (name + email). The paid
// equivalent is Purchase; this is what an ad for a free class optimises on.
// Browser-side on purpose: the server's CompleteRegistration goes through the
// Conversions API, which only fires once META_CAPI_ACCESS_TOKEN is set, and the
// account that a free registration creates never passes through /register.
export function trackFreeRegistration(product: { _id: string; title?: string }): void {
  trackPixel('CompleteRegistration', {
    content_ids: [product._id],
    content_type: 'product',
    content_name: product.title,
    value: 0,
    currency: 'USD',
    status: 'free_event',
  });
}

// The event that was missing. Meta had PageView, ViewContent and InitiateCheckout
// but zero Purchases, because checkout finishes on Stripe's hosted page and
// nothing fired when the money actually landed — which meant no purchase
// optimisation, no buyer lookalike, and no way to read ROAS.
//
// Fired on the return-from-Stripe page, and deduplicated against the server's
// Conversions API copy by sharing one eventId (Stripe's session id): Meta keeps
// whichever arrives first and discards the twin, so the browser covers the fast
// path and the server covers the closed-tab-and-ad-blocker path.
export function trackPurchase(args: {
  eventId: string;            // the Stripe checkout session id
  productId: string;
  title?: string;
  valueCents?: number;
}): void {
  if (!pixelLoaded) return;
  // Fire once per checkout session even if the success page is reloaded or
  // the back button returns to it.
  const seenKey = `hj_pixel_purchase_${args.eventId}`;
  try {
    if (localStorage.getItem(seenKey)) return;
    localStorage.setItem(seenKey, '1');
  } catch { /* private mode — firing twice beats not firing */ }
  window.fbq?.('track', 'Purchase', {
    content_ids: [args.productId],
    content_type: 'product',
    content_name: args.title,
    value: (args.valueCents || 0) / 100,
    currency: 'USD',
  }, { eventID: args.eventId });
}
